import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Checkbox } from '@/components/ui/Checkbox';
import Button from '@/components/ui/Button';
import StatusBadge from '@/components/ui/StatusBadge';
import { Spinner } from '@/components/ui/Spinner';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/DropdownMenu';
import AppIcon from '@/components/AppIcon';
import Pagination from '@/components/Pagination';

// Define the interface for the employee data THIS COMPONENT EXPECTS
export interface Employee {
  id: number;
  userId: string;
  name: string;
  employeeId: string;
  department: string;
  employmentStatus: string;
  netPay: number;
  payslipStatus: string;
  payslipUrl: string | null;
  basicSalary: number;
  variablePay: number;
  /** Total addition-type adjustments (positive value) */
  adjustmentAdditions?: number;
  /** Total deduction-type adjustments (positive value) */
  adjustmentDeductions?: number;
  /** Net adjustments = adjustmentAdditions - adjustmentDeductions */
  adjustments?: number;
  deductions: number;
  payrollDate: string;
  createdOn: string;
  email: string;
  currencyCode?: string;
}

// Define props with default values in the function signature
interface PayslipsSectionProps {
  employees?: Employee[];
  payrollCycles?: string[];
  selectedCycle?: string;
  selectedPayslips?: number[];
  isLoading?: boolean;
  searchTerm?: string;
  currencySymbol?: string;
  currentPage?: number;
  totalCount?: number;
  totalPages?: number;
  pageSize?: number;
  onCycleChange?: (cycle: string) => void;
  onPayslipSelection?: (ids: number[]) => void;
  onPayslipPreview?: (employee: Employee) => void;
  onPayslipDownload?: (employee: Employee) => Promise<void>;
  onPayslipGenerate?: (employee: Employee) => Promise<void>;
  onPayslipRegenerate?: (employee: Employee) => Promise<void>;
  onPayslipNotify?: (employee: Employee) => Promise<void>;
  onCreatePayslip?: (employee: Employee) => Promise<void>;
  onPageChange?: (page: number) => void;
}

const PayslipsSection: React.FC<PayslipsSectionProps> = ({
  // Provide safe default values for EVERY prop
  employees = [],
  payrollCycles = [],
  selectedCycle = '',
  selectedPayslips = [],
  isLoading = false,
  searchTerm: externalSearchTerm,
  currencySymbol = 'AED',
  currentPage = 1,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  totalCount = 0,
  totalPages = 1,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pageSize = 25,
  onCycleChange = () => {},
  onPayslipSelection = () => {},
  onPayslipPreview = () => {},
  onPayslipDownload = async () => {},
  onPayslipGenerate = async () => {},
  onPayslipRegenerate = async () => {},
  onPayslipNotify = async () => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCreatePayslip = async () => {},
  onPageChange = () => {},
}) => {
  const { t } = useTranslation();
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [selectedAll, setSelectedAll] = useState(false);

  // CRITICAL SAFETY CHECK: Ensure employees is always an array
  const safeEmployees = Array.isArray(employees) ? employees : [];

  // Use external searchTerm if provided, otherwise use internal
  const effectiveSearch = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;

  // Filter employees based on search term (with safety checks)
  const filteredEmployees = safeEmployees.filter(employee => {
    const name = employee?.name || '';
    const empId = employee?.employeeId || '';
    const dept = employee?.department || '';
    const email = employee?.email || '';
    const term = effectiveSearch.toLowerCase();

    return name.toLowerCase().includes(term) ||
           empId.toLowerCase().includes(term) ||
           dept.toLowerCase().includes(term) ||
           email.toLowerCase().includes(term);
  });

  // Handle select all
  const handleSelectAll = () => {
    if (!selectedAll && safeEmployees.length > 0) {
      const allIds = safeEmployees.map(emp => emp.id).filter(id => id != null);
      onPayslipSelection(allIds);
      setSelectedAll(true);
    } else {
      onPayslipSelection([]);
      setSelectedAll(false);
    }
  };

  // Handle single select
  const handleSelect = (id: number) => {
    const newSelected = selectedPayslips.includes(id)
      ? selectedPayslips.filter(payslipId => payslipId !== id)
      : [...selectedPayslips, id];
    onPayslipSelection(newSelected);
    setSelectedAll(newSelected.length === safeEmployees.length && safeEmployees.length > 0);
  };

  // Get status badge variant
  const getStatusVariant = (status: string): 'success' | 'error' | 'warning' | 'info' | 'neutral' => {
    if (!status) return 'neutral';

    switch (status.toLowerCase()) {
      case 'generated':
        return 'success';
      case 'notified':
      case 'sent':
        return 'info';
      case 'pending':
        return 'warning';
      case 'draft':
        return 'neutral';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'neutral';
    }
  };

  // Format currency -- uses per-employee currency code when available
  const formatCurrency = (amount: number, currency?: string) => {
    return `${currency || currencySymbol} ${(amount || 0).toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return t('N/A');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return t('Invalid Date');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px] gap-3">
        <Spinner size="md" />
        <span className="text-sm text-muted-foreground">{t('Loading payslips...')}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Main table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedAll && safeEmployees.length > 0}
                    indeterminate={selectedPayslips.length > 0 && selectedPayslips.length < safeEmployees.length}
                    onChange={handleSelectAll}
                    disabled={safeEmployees.length === 0}
                  />
                </TableHead>
                <TableHead>{t('Employee')}</TableHead>
                <TableHead>{t('Department')}</TableHead>
                <TableHead>{t('Gross Total')}</TableHead>
                <TableHead>{t('Deductions')}</TableHead>
                <TableHead>{t('Net Pay')}</TableHead>
                <TableHead>{t('Status')}</TableHead>
                <TableHead>{t('Payroll Date')}</TableHead>
                <TableHead className="text-end">{t('Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <p className="text-muted-foreground">
                      {effectiveSearch ? t('No matching employees found') : t('No payslips available')}
                    </p>
                    {effectiveSearch && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setInternalSearchTerm('')}
                        className="mt-2"
                      >
                        {t('Clear search')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee) => (
                  <TableRow
                    key={employee.id}
                    className={selectedPayslips.includes(employee.id) ? 'bg-muted/50' : 'hover:bg-muted/30'}
                  >
                    <TableCell className="w-12">
                      <Checkbox
                        checked={selectedPayslips.includes(employee.id)}
                        onChange={() => handleSelect(employee.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {employee.name || t('Unknown')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {employee.email || t('No email')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {employee.department || t('N/A')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {formatCurrency(
                          (employee.basicSalary || 0) + (employee.variablePay || 0) + (employee.adjustments ?? 0),
                          employee.currencyCode
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {formatCurrency(employee.deductions || 0, employee.currencyCode)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium text-foreground">
                        {formatCurrency(employee.netPay || 0, employee.currencyCode)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        data-testid="status-badge"
                        variant={getStatusVariant(employee.payslipStatus)}
                        size="sm"
                      >
                        {employee.payslipStatus || t('Pending')}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {formatDate(employee.payrollDate)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end items-center gap-1">
                        <button
                          title={t('Preview')}
                          className="p-1.5 rounded-md hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          onClick={() => onPayslipPreview(employee)}
                          disabled={isLoading}
                        >
                          <AppIcon name="Eye" size={16} className="text-muted-foreground" />
                        </button>

                        <button
                          title={t('Download PDF')}
                          data-testid="download-btn"
                          className="p-1.5 rounded-md hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
                          onClick={() => onPayslipDownload(employee)}
                          disabled={employee.payslipStatus !== 'Generated' || isLoading}
                        >
                          <AppIcon name="Download" size={16} className="text-muted-foreground" />
                        </button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              title={t('More actions')}
                              className="p-1.5 rounded-md hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              disabled={isLoading}
                            >
                              <AppIcon name="MoreVertical" size={16} className="text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent sideOffset={4}>
                            {/* Regenerate Payslip */}
                            <DropdownMenuItem
                              onSelect={() => onPayslipRegenerate(employee)}
                            >
                              <AppIcon name="RefreshCw" size={14} className="me-2 text-primary" />
                              {t('Regenerate Payslip')}
                            </DropdownMenuItem>

                            {/* Generate PDF */}
                            <DropdownMenuItem
                              onSelect={() => onPayslipGenerate(employee)}
                              disabled={employee.payslipStatus === 'Generated'}
                            >
                              <AppIcon name="FileText" size={14} className="me-2 text-primary" />
                              {t('Generate PDF')}
                            </DropdownMenuItem>

                            {/* Notify Employee */}
                            <DropdownMenuItem
                              data-testid="notify-btn"
                              onSelect={() => onPayslipNotify(employee)}
                              disabled={employee.payslipStatus !== 'Generated' || employee.payslipStatus === 'Notified'}
                            >
                              <AppIcon name="Bell" size={14} className="me-2 text-primary" />
                              {t('Notify Employee')}
                            </DropdownMenuItem>

                            <div className="border-t border-border my-1" />

                            {/* Download */}
                            <DropdownMenuItem
                              onSelect={() => onPayslipDownload(employee)}
                              disabled={!['Generated', 'Notified'].includes(employee.payslipStatus)}
                            >
                              <AppIcon name="Download" size={14} className="me-2 text-muted-foreground" />
                              {t('Download')}
                            </DropdownMenuItem>

                            {/* Preview */}
                            <DropdownMenuItem
                              onSelect={() => onPayslipPreview(employee)}
                            >
                              <AppIcon name="Eye" size={14} className="me-2 text-muted-foreground" />
                              {t('Preview')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="border-t border-border px-4 py-3" data-testid="pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              variant="compact"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PayslipsSection;
